import { ArrowRight, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { CourseCompletionQuizSummary } from "@/types/courseCompletionQuiz";

interface CourseCompletionQuizIntroProps {
  summary: CourseCompletionQuizSummary;
  courseTitle: string;
  starting?: boolean;
  /** Why the last start did not work, ready to show. */
  error?: string | null;
  onStart: () => void;
  onBack: () => void;
}

const STEPS = [
  {
    title: "Eine Frage nach der anderen",
    text: "Du beantwortest die Fragen der Reihe nach.",
  },
  {
    title: "Rückmeldung am Ende",
    text: "Ob deine Antworten richtig waren, siehst du erst in der Auswertung, nach der letzten Frage.",
  },
  {
    title: "Zurück zum Dashboard",
    text: "Du kannst zum Dashboard zurückgehen und später über „Fortsetzen“ weitermachen.",
  },
  {
    title: "Auswertung",
    text: "Am Ende siehst du dein Ergebnis und wie es weitergeht.",
  },
];

/** Introduction before a run: what the quiz is, its rules, and the start button. */
export const CourseCompletionQuizIntro = ({
  summary,
  courseTitle,
  starting = false,
  error,
  onStart,
  onBack,
}: CourseCompletionQuizIntroProps) => {
  const nextAttempt = summary.attemptsUsed + 1;

  return (
    <div>
      <h1 className="mb-3">Zeig, was du im Kurs gelernt hast</h1>
      <div
        aria-hidden="true"
        className="mb-3 h-1 w-24 rounded-full bg-linear-to-r from-primary to-secondary"
      />
      <p>
        Das Quiz fragt Inhalte aus dem ganzen Kurs ab.
        {` Bestehst du, gilt „${courseTitle}“ als abgeschlossen.`}
      </p>

      <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_320px]">
        <section>
          <h2>So läuft das Quiz ab</h2>
          <ol className="mt-4 flex flex-col gap-4">
            {STEPS.map((step, index) => (
              <li key={step.title} className="flex gap-3">
                <span
                  aria-hidden="true"
                  className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground"
                >
                  {index + 1}
                </span>
                <div>
                  <p className="font-semibold text-primary">{step.title}</p>
                  <p>{step.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <aside className="rounded-xl bg-info-light p-5">
          <p className="flex items-center gap-2 text-info-foreground">
            <Info className="size-[1em] shrink-0" aria-hidden="true" />
            Gut zu wissen
          </p>
          <ul className="mt-3 flex list-disc flex-col gap-2 pl-5 text-foreground">
            <li>Ein Versuch zählt ab dem Start des Quiz.</li>
            <li>Es gibt kein Zeitlimit.</li>
            <li>
              Bestehst du nicht, kannst du nach {summary.retryWaitHours} Stunden
              einen neuen Versuch starten – höchstens {summary.maxAttempts}{" "}
              Versuche insgesamt.
            </li>
          </ul>
        </aside>
      </div>

      {error && (
        <p role="alert" className="mt-6 font-semibold text-destructive">
          {error}
        </p>
      )}

      <div className="mt-8 flex flex-wrap items-end justify-end gap-3">
        <Button type="button" variant="secondary" size="lg" onClick={onBack}>
          Zurück zum Dashboard
        </Button>
        <div className="flex flex-col items-end gap-3">
          {/* Same end inset as the start button, so the label meets the arrow. */}
          <p className="pr-4 text-sm text-muted-foreground">
            Versuch {nextAttempt} von {summary.maxAttempts}
          </p>
          <Button type="button" size="lg" disabled={starting} onClick={onStart}>
            Quiz starten
            <ArrowRight aria-hidden="true" />
          </Button>
        </div>
      </div>
    </div>
  );
};
